// A-1 / B-7「先驗證你的 email」（kq Q143；字串表 2026-10-28-prod/a1-b7-strings.md 第 1 段）：新增登入方式（通行密鑰、兩步驟、
// API 金鑰、App 帳號連結）時 identity 回 403 EMAIL_NOT_VERIFIED（detail.resend_available）→ 這個對話框：
// 〔寄驗證信〕= BFF /session/email/verify-send → identity POST /v1/auth/email/verify/send、〔稍後再說〕。
// 寄出 → 提示到信箱點連結後再試；其實已經驗證過 → 關掉、呼叫 onVerified（呼叫端重試原本的動作）。
// 429 RATE_LIMITED = 這個帳號 60 秒內已經寄過一封（註冊時就會自動寄）→ 一樣顯示「已寄出」（home-base 三平台同，kq Q143 10-26 實機）。
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from './Dialog';
import { SessionError, sendEmailVerification, session } from '../lib/session';

/** 這個錯誤要不要開「先驗證你的 email」：回 { resend } = detail.resend_available（沒帶 → true）；不是這個錯誤 → null。 */
export function emailVerifyRequired(e: unknown): { resend: boolean } | null {
  if (!(e instanceof SessionError || e instanceof KinioError) || e.code !== 'EMAIL_NOT_VERIFIED') return null;
  return { resend: e.detail?.resend_available !== false };
}

/**
 * 頁面用的一行接法（platform#176：建立對外給權限的東西——邀請、SCIM、BYO AI、本地 VMS / Node 核准、接受邀請——遇 403 EMAIL_NOT_VERIFIED）：
 * `ask(e)` 是這個錯誤就開對話框、回 true（呼叫端不再顯示一般錯誤）；`prompt` 放進畫面；`onVerified` = 其實已經驗證過時重試原本的動作。
 */
export function useEmailVerifyPrompt(onVerified?: () => void): { ask(e: unknown): boolean; prompt: ReactNode } {
  const [ev, setEv] = useState<{ resend: boolean } | null>(null);
  const ask = useCallback((e: unknown) => {
    const r = emailVerifyRequired(e);
    if (r) setEv(r);
    return r !== null;
  }, []);
  const prompt = ev ? <EmailVerifyPrompt action resend={ev.resend} onClose={() => setEv(null)} onVerified={onVerified} /> : null;
  return { ask, prompt };
}

/** `action` = 這次被擋的是邀請、核准、建 token 之類的動作（platform#176），內文不講「新增登入方式」。 */
export function EmailVerifyPrompt({ resend, onClose, onVerified, action }: { resend: boolean; onClose(): void; onVerified?(): void; action?: boolean }) {
  const { t } = useTranslation();
  const email = session.get()?.user?.email ?? '';
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    try {
      if (await sendEmailVerification()) setResult(t('email_verify.sent'));
      else {
        // 其實已經驗證過：更新 session（之後不再先跳對話框），有重試就直接重試、沒有就告訴使用者再試一次（審查 P3-5）
        const cur = session.get();
        if (cur?.user) session.set({ ...cur, user: { ...cur.user, email_verified: true } });
        if (onVerified) {
          onClose();
          onVerified();
        } else setResult(t('email_verify.already'));
      }
    } catch (x) {
      const c = x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
      if (c === 'RATE_LIMITED') setResult(t('email_verify.sent'));
      else setResult(t(`email_verify.err_${c}`, { defaultValue: t('email_verify.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open title={t('email_verify.title')} onClose={onClose} testId="email-verify-dialog">
      {result ? (
        <>
          <p data-testid="email-verify-result">{result}</p>
          <div className="row-end">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {t('common.done')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p>{action ? (email ? t('email_verify.body_action', { email }) : t('email_verify.body_action_no_email')) : email ? t('email_verify.body', { email }) : t('email_verify.body_no_email')}</p>
          <div className="row-end">
            <button type="button" className="btn" disabled={busy} onClick={onClose}>
              {t('email_verify.later')}
            </button>
            {resend && (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void send()} data-testid="email-verify-send">
                {t('email_verify.send')}
              </button>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}
